<template>

  <div
    id="admin-menu"
    :class="[
      'lg:block shrink-0 border-r border-white/10 select-none z-50 bg-gray-900',
      'transition-[width] duration-300 ease-in-out',
      isCollapsed ? 'lg:w-12 w-72' : 'w-72',
      mobileMenu ? 'fixed h-full' : 'hidden h-screen',
    ]"
  >
    <div class="flex flex-col h-full">

      <div class="relative">
        <div
          class="lg:flex lg:absolute hidden top-5 -right-0.5 justify-center cursor-pointer z-2 transition-transform duration-200 hover:scale-110"
          @click="isCollapsed = !isCollapsed"
        >
          <span>
            <i :class="[
              'text-base/none text-black border border-black p-1 rounded-full absolute -right-2 bg-white fa-solid',
              'transition-transform duration-300',
              isCollapsed ? 'fa-arrow-right' : 'fa-arrow-left'
            ]" />
          </span>
        </div>
      </div>

      <div key="logo" class="flex items-center px-10 py-10 shrink-0" :class="{ 'lg:hidden': isCollapsed }">
        <router-link to="/" class="flex items-center">
          <img :src="AdminLogo" alt="Clubs Logo" />
        </router-link>
      </div>

      <div key="menu" class="overflow-y-auto space-y-2" :class="{ 'lg:hidden': isCollapsed }">

        <span class="text-white text-xs font-bold px-2 py-1.5 ml-5">OVERVIEW</span>
        <div class="pr-8 my-1.5">
          <router-link
            v-ripple
            to="/admin"
            active-class="active"
            exact-active-class="active"
            class="flex items-center cursor-pointer text-white py-2 pl-7 hover:border-l-4 hover:border-blue-400 hover:bg-wusa-navy-100 hover:text-wusa-navy-800 rounded-r-4xl transition-all duration-150 text-sm overflow-hidden"
          >
            <span class="truncate">At a Glance</span>
          </router-link>
        </div>

        <div v-for="section in visibleItems" :key="section.key">
            <button
              class="flex items-center w-full cursor-pointer text-white px-2 py-2 font-bold text-xs hover:text-gray-300 transition-colors duration-150"
              @click="toggleSection(section.key)"
            >
              <i
                class="fa fa-chevron-down text-white mr-2 transition-transform duration-200"
                :class="{ 'rotate-180': expandedKeys[section.key] }"
              />
              <span class="ml-2 truncate">{{ section.label.toUpperCase() }}</span>
            </button>

            <div
              class="overflow-hidden transition-[max-height] duration-300 ease-in-out pr-8"
              :style="expandedKeys[section.key] ? 'max-height: 600px' : 'max-height: 0'"
            >
              <router-link
                v-for="item in section.items"
                :key="item.label"
                v-ripple
                v-slot="{ href, navigate, isActive, isExactActive }"
                :to="item.route"
                custom
              >
                <a
                  :href="href"
                  :class="[
                    'flex items-center cursor-pointer text-white py-2 pl-9 rounded-r-4xl transition-all duration-150 text-sm overflow-hidden',
                    isItemActive(item, isActive, isExactActive)
                      ? 'active'
                      : 'hover:border-l-4 hover:border-blue-400 hover:bg-wusa-navy-100 hover:text-wusa-navy-800',
                  ]"
                  @click="navigate"
                >
                  <span class="truncate">{{ item.label }}</span>
                </a>
              </router-link>
            </div>
        </div>

      </div>

      <div class="mt-auto border-t border-white/10">
        <ul class="p-2 m-0 overflow-hidden">
          <li>
            <router-link
              :to="{ name: 'my-profile-notifications' }"
              class="flex items-center cursor-pointer p-3 text-white transition-colors duration-150 hover:text-gray-300"
            >
              <i class="fa-solid fa-bell mr-1" />
              <span :class="{ 'lg:hidden': isCollapsed }" class="font-medium">Notifications</span>
              <Badge
                v-if="notificationStore.hasUnread && !isCollapsed"
                :value="notificationStore.unreadCount"
                severity="danger"
                class="ml-2"
                size="small"
              />
            </router-link>
          </li>
          <li>
            <router-link
              to="/"
              class="flex items-center cursor-pointer p-3 text-white transition-colors duration-150 hover:text-gray-300"
            >
              <i class="fa-solid fa-globe mr-1" />
              <span :class="{ 'lg:hidden': isCollapsed }" class="font-medium">Public Clubs Website</span>
            </router-link>
          </li>
          <li>
            <a
              class="flex items-center cursor-pointer p-3 text-white transition-colors duration-150 hover:text-gray-300 text-sm/6"
              @click.prevent="logoutUser"
            >
              <i class="fa-solid fa-sign-out mr-1" />
              <span :class="{ 'lg:hidden': isCollapsed }" class="font-medium">Sign Out</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <Transition
    enter-active-class="transition-opacity duration-200 ease-out"
    leave-active-class="transition-opacity duration-150 ease-in"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="mobileMenu"
      class="fixed inset-0 bg-black/60 z-40 lg:hidden"
      @click="mobileMenu = false"
    />
  </Transition>

  <div class="w-full relative bg-gray-50 inline-block lg:hidden pt-3 px-3 z-1">
    <div class="flex flex-row items-center">
      <div class="text-wusa-navy-900 p-2 cursor-pointer p-ripple transition-colors duration-150 hover:text-wusa-navy-600" @click="mobileMenu = !mobileMenu">
        <i class="fa-solid fa-bars" />
      </div>
      <div class="p-2">
        <img :src="AdminTopLogo" alt="Clubs Logo" style="width:6.25rem" />
      </div>
      <div class="ml-auto flex items-center gap-3 pr-1">
        <router-link :to="{ name: 'my-profile-notifications' }" class="relative text-wusa-navy-900 hover:text-wusa-navy-600">
          <i class="fa-solid fa-bell text-lg"></i>
          <Badge
            v-if="notificationStore.hasUnread"
            :value="notificationStore.unreadCount"
            severity="danger"
            class="absolute -top-2 -right-3"
            size="small"
          />
        </router-link>
        <UserAvatar />
      </div>
    </div>
  </div>
</template>

<script setup>
import AdminLogo from '@/images/clubs-admin-logo.svg';
import AdminTopLogo from '@/images/clubs-logo-primary.svg';
import Badge from 'primevue/badge';
import { useAdminSidebar } from '@/composables/useAdminSidebar.js';
import AuthService from '@services/auth/authService.js';
import { adminMenuConfig } from '@/layouts/components/admin/adminMenuConfig.js'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { useNotificationStore } from '@/stores/notification';

const notificationStore = useNotificationStore();

const {
  isCollapsed,
  mobileMenu,
  expandedKeys,
  visibleItems,
  isItemActive,
  toggleSection,
} = useAdminSidebar(adminMenuConfig);

const logoutUser = async () => {
  try {
    await AuthService.logout();
  } catch {
    // silently ignore
  }
};
</script>

<style scoped>
.active {
  background: var(--p-wusa-blush-100);
  color: var(--p-wusa-blush-600);
  border-radius: 0 2rem 2rem 0;
  border: 0;
}
</style>
